import type { QueryKey } from '@tanstack/react-query';

import useInfiniteQuery from '../../../ReactQuery/useInfiniteQuery';
import { get } from '../../../redux/api';
import { nuc } from '../../../utils';
import type { GetMatchesResponse } from '../../../../shared/types/routes/binary';

export const getComponentMatchesQueryKey = (
  revisionLocator: string,
  componentId: string,
  pageSize: number,
): QueryKey => ['binary', revisionLocator, componentId, 'matches', pageSize];

function fetchComponentMatches({ pageParam = 0 }, revisionLocator: string, componentId: string, pageSize: number) {
  return get<GetMatchesResponse>(`/api/binary/${nuc(revisionLocator)}/${nuc(componentId)}/matches`, false, {
    pageSize,
    page: pageParam,
  });
}

/**
 * Helper hook for fetching the matches of a given component in a user's revision.
 * @param revisionLocator - The revision locator of the user's revision.
 * @param componentId - The ID of the component to fetch matches for.
 * @param pageSize - The number of matches to fetch per page.
 * @param shouldFetch - Whether to fetch the matches or not. Used to prevent fetching until matches are needed to be displayed.
 * @returns An infinite query for the matches of the given component.
 */
export default function useGetComponentMatches(
  revisionLocator: string,
  componentId: string,
  pageSize: number,
  shouldFetch: boolean = true,
) {
  return useInfiniteQuery<GetMatchesResponse>({
    queryKey: getComponentMatchesQueryKey(revisionLocator, componentId, pageSize),
    queryFn: (context) => fetchComponentMatches(context, revisionLocator, componentId, pageSize),
    enabled: shouldFetch,
  });
}
